import axios from "axios";

const API = process.env.REACT_APP_API_URL;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const BASE = `${API}/calibracion-revision`;

export const listarElegibles = (params) =>
  axios.get(`${BASE}/elegibles`, { ...auth(), params }).then((r) => r.data.items);

export const listarRolesFirmante = () =>
  axios.get(`${BASE}/roles`, auth()).then((r) => r.data);

export const asignarSegundoFirmante = (datos) =>
  axios.post(`${BASE}/asignar`, datos, auth()).then((r) => r.data);

export const listarRevisionesPendientes = () =>
  axios.get(`${BASE}/pendientes`, auth()).then((r) => r.data);

export const firmarRevisiones = (datos) =>
  axios.post(`${BASE}/firmar`, datos, auth()).then((r) => r.data);

export const devolverCalibracion = (cronogramaId, motivo) =>
  axios
    .post(`${BASE}/${cronogramaId}/devolver`, { motivo }, auth())
    .then((r) => r.data);

// Firma guardada en el perfil; el servidor solo acepta imágenes en base64
export const obtenerFirmaRegistrada = () =>
  axios.get(`${API}/users/me`, auth()).then((r) => {
    const firma = r.data?.firma ?? r.data?.user?.firma;
    return /^data:image\/(png|jpeg);base64,/.test(firma || "") ? firma : null;
  });

const fechaLocal = (fecha) =>
  [
    fecha.getFullYear(),
    String(fecha.getMonth() + 1).padStart(2, "0"),
    String(fecha.getDate()).padStart(2, "0"),
  ].join("-");

// Desde hoy menos un mes calendario hasta hoy (31 mar → 28 feb)
export const rangoUltimoMes = (hoy = new Date()) => {
  const desde = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
  const ultimoDia = new Date(desde.getFullYear(), desde.getMonth() + 1, 0).getDate();
  desde.setDate(Math.min(hoy.getDate(), ultimoDia));
  return { desde: fechaLocal(desde), hasta: fechaLocal(hoy) };
};

export const estadoServicio = (s) =>
  ["Completado", "Cerrado"].includes(s.estado) ? "Finalizada" : s.estado;

const sinTildes = (texto) =>
  String(texto ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const idDe = (ref) => ref?._id || ref || "";

const coincide = (seleccion, valor) =>
  !seleccion?.length || seleccion.includes(idDe(valor));

export const filtrarElegibles = (
  filas,
  { cliente, sedes, areas, metrologo, estado, busqueda } = {},
) => {
  const termino = sinTildes(busqueda).trim();
  return filas.filter((s) => {
    const e = s.equipo || {};
    if (cliente && idDe(e.cliente) !== cliente) return false;
    if (!coincide(sedes, e.sede) || !coincide(areas, e.area)) return false;
    if (metrologo && idDe(s.metrologo) !== metrologo) return false;
    if (estado && estadoServicio(s) !== estado) return false;
    if (!termino) return true;
    return [e.serial, e.codigo, e.cliente?.name, e.marca?.name, e.modelo?.name].some(
      (campo) => sinTildes(campo).includes(termino),
    );
  });
};

const unicos = (referencias) =>
  [...new Map(
    referencias.filter((r) => r?._id).map((r) => [r._id, r]),
  ).values()];

// Opciones que tienen datos en el rango, en cascada cliente → sede → área
export const opcionesFiltro = (filas, { cliente, sedes } = {}) => {
  const delCliente = cliente
    ? filas.filter((s) => idDe(s.equipo?.cliente) === cliente)
    : filas;
  const deLasSedes = delCliente.filter((s) => coincide(sedes, s.equipo?.sede));
  return {
    clientes: unicos(filas.map((s) => s.equipo?.cliente)),
    sedes: unicos(delCliente.map((s) => s.equipo?.sede)),
    areas: unicos(deLasSedes.map((s) => s.equipo?.area)),
    metrologos: unicos(filas.map((s) => s.metrologo)),
  };
};

// Mis servicios y el formulario de ejecución exigen servicios.gestionar
export const puedeSerFirmante = (usuario) =>
  (usuario?.role?.permissions || []).some((p) =>
    ["*", "servicios.*", "servicios.gestionar"].includes(p),
  );

// Geolocalización, IP y dispositivo: los mismos datos que la autorización de servicios
export const capturarMetadatosFirma = async () => {
  const ubicacion = new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 5000 },
    );
  });
  const ip = fetch("https://api.ipify.org?format=json")
    .then((res) => res.json())
    .then((data) => data.ip || null)
    .catch(() => null);

  return {
    ubicacion: await ubicacion,
    ip: await ip,
    dispositivo: navigator.userAgent || "Desconocido",
  };
};
